import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { galleryMediaKey, prepareGalleryImage, prepareMediaImage } from '@theme/suit-shop-media-transition';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    if (this.closest('.suit-shop-product-section')) {
      this.#serverMediaKey ??= galleryMediaKey(this);
      target?.addEventListener('change', this.#handleVariantChange, { signal });
      target?.addEventListener('pointerover', this.#preloadVariantImage, { signal });
      target?.addEventListener('focusin', this.#preloadVariantImage, { signal });
      // Touch devices have no hover: start loading the moment a finger touches a color.
      target?.addEventListener('pointerdown', this.#preloadVariantImage, { signal });
      target?.addEventListener('touchstart', this.#preloadVariantImage, { signal, passive: true });
      this.#scheduleVariantImagePreload();
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #selectionGeneration = 0;
  #serverMediaKey = null;

  #variantInput(event) {
    if (!(event.target instanceof Element)) return;
    const input = event.target.closest('label')?.querySelector('input[data-featured-media-id]') ?? event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.featuredMediaId) return;
    const picker = input.closest('variant-picker');
    if (!picker || picker.dataset.productId !== this.dataset.productId || picker.closest('product-card, quick-add-dialog')) return;
    const connectedUrl = input.dataset.connectedProductUrl;
    if (connectedUrl && connectedUrl.split('?')[0] !== picker.dataset.productUrl?.split('?')[0]) return;
    return input;
  }

  #mediaSlide(mediaId) {
    return [...this.querySelectorAll('slideshow-slide.product-media-container--image')]
      .find((slide) => slide.querySelector('.product-media')?.dataset.mediaId === mediaId);
  }

  #preloadVariantImage = (event) => {
    const input = this.#variantInput(event);
    const image = input && this.#mediaSlide(input.dataset.featuredMediaId)?.querySelector('.product-media__image');
    if (image instanceof HTMLImageElement) {
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.decode?.().catch(() => {});
    }
  };

  /**
   * Warm every color's photo once the page is idle, so a click shows it instantly; then the rest of the
   * gallery, in slide order, so swiping forward on a phone never lands on a photo that is still loading.
   */
  #scheduleVariantImagePreload() {
    const run = () => {
      if (!this.isConnected) return;
      const section = this.closest('.shopify-section');
      const inputs = section?.querySelectorAll('variant-picker input[data-featured-media-id]') ?? [];
      const seen = new Set();
      inputs.forEach((input) => {
        const mediaId = input.dataset.featuredMediaId;
        if (!mediaId || seen.has(mediaId)) return;
        seen.add(mediaId);
        const image = this.#mediaSlide(mediaId)?.querySelector('.product-media__image');
        if (!(image instanceof HTMLImageElement) || image.complete) return;
        image.loading = 'eager';
        image.fetchPriority = 'low';
      });
      this.querySelectorAll('slideshow-slide .product-media__image').forEach((image) => {
        if (!(image instanceof HTMLImageElement) || image.complete) return;
        image.loading = 'eager';
        image.fetchPriority = 'low';
      });
    };
    const start = () => ('requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 1500));
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  #handleVariantChange = (event) => {
    const input = this.#variantInput(event);
    if (!input?.checked || this.presentation !== 'carousel') return;
    this.#selectMedia(input.dataset.featuredMediaId, this.#selectionGeneration).catch(() => {
      // The server response remains the fallback if an immediate preview is unavailable.
    });
  };

  async #selectMedia(mediaId, generation) {
    const slide = this.#mediaSlide(mediaId);
    const slideshow = this.slideshow;
    if (!slide || !slideshow?.slides?.includes(slide)) return false;
    const image = slide.querySelector('.product-media__image');
    await prepareMediaImage(image);
    if (!this.isConnected || generation !== this.#selectionGeneration) return true;
    if (!(image instanceof HTMLImageElement) || !image.naturalWidth) return false;
    const previous = slideshow.slides[slideshow.current];
    const ghost = previous && previous !== slide ? this.#swapGhost(previous) : null;
    slideshow.select(slideshow.slides.indexOf(slide), undefined, { animate: false });
    if (ghost) this.#playSwap(ghost, image);
    return true;
  }

  /** Freezes the outgoing photo as an overlay so the color change can swap rather than jump. */
  #swapGhost(previousSlide) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
    const oldImage = previousSlide.querySelector('.product-media__image');
    const container = previousSlide.closest('slideshow-container');
    if (!(oldImage instanceof HTMLImageElement) || !container) return null;
    const box = previousSlide.getBoundingClientRect();
    const origin = container.getBoundingClientRect();
    const ghost = document.createElement('div');
    ghost.className = 'ss-swap-ghost';
    ghost.style.cssText = `left:${box.left - origin.left}px;top:${box.top - origin.top}px;width:${box.width}px;height:${box.height}px;`;
    const clone = /** @type {HTMLImageElement} */ (oldImage.cloneNode());
    clone.removeAttribute('loading');
    ghost.append(clone);
    container.append(ghost);
    return ghost;
  }

  /** Old photo slides out and fades while the new one slides in from the opposite side. */
  #playSwap(ghost, newImage) {
    const shift = document.dir === 'rtl' ? -1 : 1;
    const timing = { duration: 420, easing: 'cubic-bezier(.2, .7, .2, 1)' };
    // Move the photo inside the clipped overlay, so it never slides over the thumbnails or details.
    ghost.firstElementChild?.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${-6 * shift}%)` }],
      { ...timing, fill: 'forwards' }
    );
    ghost.animate([{ opacity: 1 }, { opacity: 0 }], { ...timing, fill: 'forwards' })
      .finished.finally(() => ghost.remove());
    // Animations pause in background tabs; never leave the old photo covering the new one.
    setTimeout(() => ghost.remove(), timing.duration + 400);
    newImage.animate(
      [{ opacity: 0, transform: `translateX(${6 * shift}%) scale(1.02)` }, { opacity: 1, transform: 'none' }],
      timing
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#selectionGeneration++;
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const generation = ++this.#selectionGeneration;
    const preserveUnchangedMedia = Boolean(this.closest('.suit-shop-product-section'));
    if (preserveUnchangedMedia) this.setAttribute('aria-busy', 'true');

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html || !this.isConnected || generation !== this.#selectionGeneration) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        if (!preserveUnchangedMedia) {
          this.replaceWith(newMediaGallery);
          return;
        }
        const currentKey = galleryMediaKey(this);
        const incomingKey = galleryMediaKey(newMediaGallery);
        if (currentKey && this.#serverMediaKey === incomingKey) return;

        // Reuse the carousel and thumbnails when only the featured image order changed.
        if (currentKey && this.presentation === 'carousel'
            && galleryMediaKey(this, true) === galleryMediaKey(newMediaGallery, true)) {
          const mediaId = newMediaGallery.querySelector('slideshow-slide .product-media')?.dataset.mediaId;
          if (await this.#selectMedia(mediaId, generation)) {
            if (generation === this.#selectionGeneration) this.#serverMediaKey = incomingKey;
            return;
          }
        }

        // Keep the shared server HTML intact for price, availability and cart listeners.
        // Keep the current photo visible until the replacement is ready, then swap without animation.
        const preparedGallery = document.importNode(newMediaGallery, true);
        await prepareGalleryImage(preparedGallery);
        if (!this.isConnected || generation !== this.#selectionGeneration) return;
        this.replaceWith(preparedGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      })
      .finally(() => {
        if (generation === this.#selectionGeneration) this.removeAttribute('aria-busy');
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
